import { motion } from 'framer-motion';

interface AnimatedBackgroundProps {
  type?: 'floating-shapes' | 'gradient' | 'particles' | 'waves';
  className?: string;
  children?: React.ReactNode;
}

export default function AnimatedBackground({ 
  type = 'floating-shapes', 
  className = '',
  children 
}: AnimatedBackgroundProps) {
  const floatingShapes = (
    <>
      {/* Floating circles */}
      <motion.div
        className="absolute top-1/4 left-1/4 w-16 h-16 bg-blue-200 rounded-full opacity-20"
        animate={{ 
          y: [0, -30, 0],
          x: [0, 20, 0],
          scale: [1, 1.2, 1],
          rotate: [0, 180, 360]
        }}
        transition={{ 
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute top-3/4 right-1/4 w-12 h-12 bg-orange-200 rounded-full opacity-30"
        animate={{ 
          y: [0, -25, 0],
          x: [0, -15, 0],
          scale: [1, 1.3, 1],
          rotate: [0, -180, -360]
        }}
        transition={{ 
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1
        }}
      />
      <motion.div
        className="absolute bottom-1/4 left-1/3 w-8 h-8 bg-yellow-200 rounded-full opacity-40"
        animate={{ 
          y: [0, -20, 0],
          x: [0, 10, 0],
          scale: [1, 1.4, 1],
          rotate: [0, 90, 180]
        }}
        transition={{ 
          duration: 7,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2
        }}
      />
      
      {/* Floating triangles */}
      <motion.div
        className="absolute top-1/2 right-1/3 w-0 h-0 border-l-[12px] border-l-transparent border-b-[20px] border-b-pink-200 border-r-[12px] border-r-transparent opacity-25"
        animate={{ 
          y: [0, -15, 0],
          rotate: [0, 120, 240],
          scale: [1, 1.1, 1]
        }}
        transition={{ 
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.5
        }}
      />
    </>
  );

  const gradientBackground = (
    <motion.div
      className="absolute inset-0 bg-gradient-to-br from-blue-50 via-amber-50 to-orange-50"
      animate={{ 
        background: [
          "linear-gradient(135deg, #eff6ff 0%, #fef3c7 50%, #fed7aa 100%)",
          "linear-gradient(135deg, #fef3c7 0%, #fed7aa 50%, #eff6ff 100%)",
          "linear-gradient(135deg, #fed7aa 0%, #eff6ff 50%, #fef3c7 100%)",
          "linear-gradient(135deg, #eff6ff 0%, #fef3c7 50%, #fed7aa 100%)"
        ]
      }}
      transition={{ 
        duration: 10,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    />
  );

  const particles = (
    <>
      {[...Array(20)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-blue-300 rounded-full opacity-60"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`
          }}
          animate={{ 
            y: [0, -100, 0],
            opacity: [0.6, 0, 0.6],
            scale: [1, 1.5, 1]
          }}
          transition={{ 
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2,
            ease: "easeInOut"
          }}
        />
      ))}
    </>
  );

  const waves = (
    <>
      <motion.div
        className="absolute bottom-0 left-0 w-full h-16 bg-gradient-to-t from-blue-200 to-transparent opacity-30"
        animate={{ 
          y: [0, -5, 0],
          scaleY: [1, 1.1, 1]
        }}
        transition={{ 
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute bottom-0 left-0 w-full h-12 bg-gradient-to-t from-orange-200 to-transparent opacity-20"
        animate={{ 
          y: [0, -3, 0],
          scaleY: [1, 1.2, 1]
        }}
        transition={{ 
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1
        }}
      />
    </>
  );

  const getBackgroundContent = () => {
    switch (type) {
      case 'gradient':
        return gradientBackground;
      case 'particles':
        return particles;
      case 'waves':
        return waves;
      default:
        return floatingShapes;
    }
  };

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {getBackgroundContent()}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
} 